import {
    ColumnFilterOption,
    ViewFilterKey,
} from '@app/shared/models/view-config.interface';
import { EventType } from 'api/models';

/**
 * Factories for the `filterConfig.filterKeys` entries that drive the column
 * header filters, mirroring the {@link ColumnCreator} pattern.
 *
 * A `keyName` is the backend query-param name AND the identity of the filter.
 * It must be a param the view's endpoint accepts - an unsupported param makes
 * the backend reject the whole request with HTTP 400.
 */

/** Boolean columns (`discrepancies`, `palletTickets`) expect these exact strings. */
export const BOOLEAN_FILTER_OPTIONS: ColumnFilterOption[] = [
    { value: 'true', label: 'TRUE' },
    { value: 'false', label: 'FALSE' },
];

export const STATUS_FILTER_OPTIONS: ColumnFilterOption[] = [
    {
        value: 'open',
        label: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.OPEN',
    },
    {
        value: 'sent',
        label: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.SENT',
    },
    {
        value: 'closed',
        label: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.CLOSED',
    },
];

/**
 * Translation-key prefix for an `EventType`. The notification table's status
 * cell, its filter options and its chip all have to resolve the same key.
 */
export const EVENT_TYPE_TRANSLATION_PREFIX =
    'OUTGOING-GOODS.NOTIFICATION-VIEW.TABLE-HEADER.TYPES.';

/**
 * The notification table's "status" is `payload.eventType`. Only the three
 * self-checkin types are ever loaded (see `EventsPaginatedService`), listed in
 * the order the driver passes through them.
 */
export const EVENT_TYPE_FILTER_OPTIONS: ColumnFilterOption[] = [
    EventType.SelfCheckinRegistrationCompleted,
    EventType.SelfCheckinDriverWaiting,
    EventType.SelfCheckinCheckinFinished,
].map(eventType => ({
    value: eventType,
    label: `${EVENT_TYPE_TRANSLATION_PREFIX}${eventType}`,
}));

/** Hint for date filters - the backend matches substrings of `DD.MM.YYYY`. */
export const DATE_FILTER_PLACEHOLDER = 'DD.MM.YYYY';

export class FilterKeyCreator {
    /** Free-text filter. `columnKey` is only needed when it differs from `keyName`. */
    static text(
        keyName: string,
        label: string,
        columnKey?: string
    ): ViewFilterKey {
        return {
            keyName,
            label,
            translationKey: label,
            type: 'text',
            ...(columnKey ? { columnKey } : {}),
        };
    }

    /** Free-text filter matched against the rendered `DD.MM.YYYY` value. */
    static date(
        keyName: string,
        label: string,
        columnKey?: string
    ): ViewFilterKey {
        return {
            keyName,
            label,
            translationKey: label,
            type: 'date',
            ...(columnKey ? { columnKey } : {}),
        };
    }

    /** Yes/no selection sending the strings `'true'` / `'false'`. */
    static boolean(
        keyName: string,
        label: string,
        columnKey?: string
    ): ViewFilterKey {
        return {
            keyName,
            label,
            translationKey: label,
            type: 'boolean',
            options: BOOLEAN_FILTER_OPTIONS,
            ...(columnKey ? { columnKey } : {}),
        };
    }

    /** Open/sent/closed selection. */
    static status(
        keyName: string,
        label: string,
        columnKey?: string
    ): ViewFilterKey {
        return {
            keyName,
            label,
            translationKey: label,
            type: 'status',
            options: STATUS_FILTER_OPTIONS,
            ...(columnKey ? { columnKey } : {}),
        };
    }

    /**
     * Selection over any other closed set of values, e.g.
     * {@link EVENT_TYPE_FILTER_OPTIONS}. Prefer this over {@link text} whenever
     * the column shows an enum - free text would let the user search for values
     * that cannot exist, and would only match the label in the current language.
     */
    static options(
        keyName: string,
        label: string,
        options: ColumnFilterOption[],
        columnKey?: string
    ): ViewFilterKey {
        return {
            keyName,
            label,
            translationKey: label,
            type: 'select',
            options,
            ...(columnKey ? { columnKey } : {}),
        };
    }
}
